/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { colors } from "@farmlands/mui-theme";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Stack,
  styled,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import React from "react";

import MaterialSymbol from "../../components/MaterialSymbol";
import { formatNumber } from "../../util/helpers";

import { formatUnitDisplay } from "./SalesLine";
import { SalesOrderApiEntity } from "./storage";

interface MobileSalesOrderCardProps {
  order: SalesOrderApiEntity;
  onViewOrder: (orderId: string) => void;
}

const ExpandLess = () => <MaterialSymbol>expand_less</MaterialSymbol>;
const ExpandMore = () => <MaterialSymbol>expand_more</MaterialSymbol>;

const BodyView = styled(Box)(({ theme }) => ({
  border: "solid",
  borderWidth: "0 1px 1px 1px ",
  borderColor: theme.colors.primary100,
  "& .MuiGrid-item": {
    padding: "4px 16px",
    "&.title": {
      "& .MuiTypography-root": {
        ...theme.typography.body2,
        color: theme.colors.grey700,
      },
    },
    "& .material-symbols-outlined": {
      fontSize: "16px",
      color: theme.colors.primary500,
      marginLeft: "4px",
    },
    "& .MuiTypography-root": {
      ...theme.typography.body2,
    },
  },
}));

const MobileSalesOrderCard: React.FC<MobileSalesOrderCardProps> = ({ order, onViewOrder }) => {
  const [open, setOpen] = React.useState(false);
  let sx = {};
  if (order.status === "Invoiced") {
    sx = {
      backgroundColor: colors.primary700,
      color: colors.shade0,
      textTransform: "uppercase",
      fontSize: "12px",
      "&:hover": {
        backgroundColor: colors.primary700,
        color: colors.shade0,
      },
    };
  } else if (order.status === "Open order") {
    sx = {
      backgroundColor: colors.primary400,
      color: colors.shade0,
      textTransform: "uppercase",
      fontSize: "12px",
      "&:hover": {
        backgroundColor: colors.primary400,
        color: colors.shade0,
      },
    };
  } else {
    sx = {
      backgroundColor: colors.grey200,
      color: colors.grey900,
      textTransform: "uppercase",
      fontSize: "12px",
      "&:hover": {
        backgroundColor: colors.grey200,
        color: colors.grey900,
      },
    };
  }

  return (
    <Card variant="outlined" sx={{ flex: 1, ml: 1 }}>
      <CardContent sx={{ mb: "0px" }}>
        {/* Month Title and Chip */}
        <Stack onClick={() => setOpen(!open)} sx={{ cursor: "pointer" }} gap={0.5}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h4">{dayjs(order.date).format("DD/MM/YYYY")}</Typography>
            <Box>
              <Chip label={order.status} sx={sx} />
              <IconButton>{open ? <ExpandLess /> : <ExpandMore />}</IconButton>
            </Box>
          </Box>
          <Box display="flex" justifyContent="space-between" sx={{ cursor: "pointer" }}>
            <Typography variant="body2" color="textSecondary">
              {order.id}
            </Typography>
          </Box>
        </Stack>
        {/* Collapsible Product List */}
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Divider />
          <BodyView>
            <Grid container>
              <Grid item className="title" xs={3}>
                <Typography>ID</Typography>
              </Grid>
              <Grid item xs={9}>
                <Typography>{order.id}</Typography>
              </Grid>
              <Grid item className="title" xs={3}>
                <Typography>Product</Typography>
              </Grid>
              <Grid item xs={9}>
                {order.products.map(({ productDescription }) => (
                  <Typography key={productDescription}>{productDescription}</Typography>
                ))}
              </Grid>
              <Grid item className="title" xs={3}>
                <Typography>Amount</Typography>
              </Grid>
              <Grid item xs={9}>
                {order.products.map(({ amount, unit }) => (
                  <Typography key={amount}>
                    {formatNumber(amount)}
                    {formatUnitDisplay(unit)}
                  </Typography>
                ))}
              </Grid>
            </Grid>
          </BodyView>

          <Button
            sx={{ mt: 2, ml: 0, pl: 1 }}
            variant="text"
            color="primary"
            onClick={() => onViewOrder(order.id)}
            startIcon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="24px"
                viewBox="0 -960 960 960"
                width="24px"
                fill={colors.primary700}
              >
                <path d="M440-280H280q-83 0-141.5-58.5T80-480q0-83 58.5-141.5T280-680h160v80H280q-50 0-85 35t-35 85q0 50 35 85t85 35h160v80ZM320-440v-80h320v80H320Zm200 160v-80h160q50 0 85-35t35-85q0-50-35-85t-85-35H520v-80h160q83 0 141.5 58.5T880-480q0 83-58.5 141.5T680-280H520Z" />
              </svg>
            }
          >
            View sales order
          </Button>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default MobileSalesOrderCard;
